import {
  CategoriesConfig,
  convertGenericViewConfigToGridViewConfig,
  SmartViewEvents,
  SmartViewsUpdateRequest,
  ValidateViewNameErrorCodeResponse,
  ValidateViewNameResponse,
} from './types';
import { SmartViewInlineActions } from '../gridSmartViews/types';
import { RequiredValidator } from '../../ui/gzForm/validators/required.validator';
import { RemoteValidator } from '../../ui/gzForm/validators/remote.validator';
import { translateFn } from '../../utils/translator.helper';
import { WebComponentEvent } from '../../framework/BaseWebComponentController';
import { convertGridConfigToGenericConfig } from '../gridView/stories/gridWithGenericSmartViews/utils';
import { GridTitleConfig } from '../uiElements/gridViewTitle/types';
import { FAVORITE_ICON_STATE } from '../utils/constants';
import { GridViewWebComponent } from '../gridView';
import { ColumnIndexes, ColumnKeys } from '../../riskManagement/userBehaviorRisksApp/types';
import { GenericGridSmartViewsWebComponent } from './component/GenericGridSmartViewsWebComponent';
import { SideNavEvents } from '../../ui/gzPushBodyLayout/types';
import { GzPushBodyLayoutWebComponent } from '../../ui/gzPushBodyLayout';
import { BaseWebComponentView } from '../../framework/BaseWebComponentView';

export function getSavedSmartViewsCategory(
  componentName: string,
  defaultViewsTranslations: object,
  validateViewNameRpc: Function
): CategoriesConfig {
  return {
    internalCategories: {
      recent: {
        isAvailable: false,
      },
      favorites: {
        isAvailable: true,
      },
      default: {
        isAvailable: true,
        translations: defaultViewsTranslations,
      },
    },
    customCategories: [
      {
        sectionTitle: translateFn('SAVED'),
        type: 'saved',
        inlineActions: [
          {
            title: 'Rename',
            value: SmartViewInlineActions.RENAME,
          },
          {
            title: 'Delete',
            value: SmartViewInlineActions.DELETE,
            customClass: 'danger',
          },
        ],
        emptyStateItemText: translateFn('No saved views yet'),
        smartViewMaxInputLength: 64,
        noSpaceEscape: true,
        smartViewValidators: [
          RequiredValidator('This field is required.'),
          RemoteValidator(
            (value: string) => {
              const viewName = value.trimEnd().trimStart();

              return validateViewNameRpc(componentName, {
                name: viewName,
              });
            },
            (response: ValidateViewNameResponse) => response.valid,
            (response: ValidateViewNameResponse) => translateValidateViewNameErrorCode(response.errorCode),
            translateFn('A communication error has occurred. Please try again later.')
          ),
        ],
        items: [],
      },
    ],
  };
}

export function translateValidateViewNameErrorCode(errorCode: number, customErrorMappingFn?: Function) {
  const errorString = typeof customErrorMappingFn !== 'undefined' ? customErrorMappingFn(errorCode) : null;

  if (errorString) {
    return errorString;
  }

  if (errorCode === ValidateViewNameErrorCodeResponse.INVALID_VIEWNAME_LENGTH_CODE) {
    return translateFn('A view name must have a maximum of 64 characters.');
  }
  if (errorCode === ValidateViewNameErrorCodeResponse.INVALID_VIEWNAME_DUPLICATE_NAME_CODE) {
    return translateFn('A view with this name already exists.');
  }
  if (errorCode === ValidateViewNameErrorCodeResponse.INVALID_VIEWNAME_INVALID_CHARACTERS) {
    return translateFn('A view name must not contain special characters.');
  }

  return null;
}

export async function updateViewProps(
  grid: GridViewWebComponent<ColumnIndexes, ColumnKeys>,
  gridSmartViews: GenericGridSmartViewsWebComponent,
  event: WebComponentEvent
) {
  const viewConfig = event.detail.viewConfiguration;

  await gridSmartViews.updateSmartView(processSmartViewForUpdate(viewConfig));
  grid.activeViewProps = event.detail.viewConfiguration;
  grid.activeViewInitialProps = event.detail.viewConfiguration;
  grid.defaultViewProps = event.detail.viewConfiguration;
}

export async function showSaveAsDialog(
  grid: GridViewWebComponent<ColumnIndexes, ColumnKeys>,
  gridSmartViews: any,
  dialogText: string
) {
  const viewConfiguration = getViewSpecifics(grid.activeViewProps);

  await gridSmartViews.saveSmartView(convertGridConfigToGenericConfig(viewConfiguration), {
    dialogText,
  });
}

export function handleSmartViewAfterDelete(
  grid: GridViewWebComponent<ColumnIndexes, ColumnKeys>,
  gridSmartViews: GenericGridSmartViewsWebComponent,
  buildGridFn: Function,
  event: WebComponentEvent
) {
  const view = event.detail.deletedView;
  if (view._id === grid.viewId) {
    const activeView = gridSmartViews.getActiveView();
    buildGridFn(convertGenericViewConfigToGridViewConfig(activeView));
  }
}

export function handleSmartViewAfterCreate(buildGridFn: Function, event: WebComponentEvent) {
  buildGridFn(convertGenericViewConfigToGridViewConfig(event.detail.createdView));
}

export function handleSmartViewNameUpdated(
  grid: GridViewWebComponent<ColumnIndexes, ColumnKeys>,
  event: WebComponentEvent
) {
  const smartViewConfig = event.detail.smartViewItemConfiguration;
  if (grid.viewId === smartViewConfig._id) {
    grid.titleConfig = new GridTitleConfig(event.detail.updatedViewName);
  }
}

export function handleSmartViewFavoriteChanged(
  grid: GridViewWebComponent<ColumnIndexes, ColumnKeys>,
  gridSmartViews: GenericGridSmartViewsWebComponent
) {
  const smartViewId = grid.viewId;
  const currentSmartView = grid.activeViewProps;
  if (currentSmartView.isReadOnly && currentSmartView.isRecent) {
    return;
  }
  let newIsFavoriteFlag: boolean;
  if (!currentSmartView.isFavorite) {
    gridSmartViews.makeSmartViewFavorite(smartViewId);
    const config = {
      disabled: false,
      icon: FAVORITE_ICON_STATE.FILLED,
      active: true,
    };
    grid.controller?.updateFavoriteIconConfig(config);
    newIsFavoriteFlag = true;
  } else {
    const config = {
      disabled: false,
      icon: FAVORITE_ICON_STATE.OUTLINE,
      active: false,
    };
    gridSmartViews.removeFromFavorite(smartViewId);
    grid.controller?.updateFavoriteIconConfig(config);
    newIsFavoriteFlag = false;
  }
  //updating all grid related view props as the current view has changed
  grid.activeViewProps.isFavorite = newIsFavoriteFlag;
  if (grid.defaultViewProps) {
    grid.defaultViewProps.isFavorite = newIsFavoriteFlag;
  }
  grid.activeViewInitialProps.isFavorite = newIsFavoriteFlag;
}

export function handleSmartViewClicked(
  grid: GridViewWebComponent<ColumnIndexes, ColumnKeys>,
  gridSmartViews: GenericGridSmartViewsWebComponent,
  buildGridFn: Function,
  event: WebComponentEvent
) {
  const smartViewConfiguration = event.detail.smartViewConfiguration;
  if (grid.viewId === event.detail.smartViewConfiguration._id) {
    return;
  }
  gridSmartViews.controller?.updateActiveViewId(event.detail.smartViewConfiguration._id);
  const convertedConfig = convertGenericViewConfigToGridViewConfig(smartViewConfiguration);
  buildGridFn(convertedConfig);
}

export function processSmartViewForUpdate(view): SmartViewsUpdateRequest {
  return {
    id: view.id,
    details: {
      specifics: getViewSpecifics(view),
    },
  };
}

function getViewSpecifics(view) {
  return {
    filters: view.filters,
    displayedColumns: view.displayedColumns,
    compactView: view.compactView,
    columnConfigs: view.columnConfigs,
    pageSize: view.pageSize,
  };
}

export function getSmartViewsListeners(
  grid: GridViewWebComponent<ColumnIndexes, ColumnKeys>,
  gridSmartViews: GenericGridSmartViewsWebComponent,
  buildGridFn: Function,
  translations: any
) {
  return [
    {
      element: grid,
      event: SmartViewEvents.SMART_VIEW_SAVED,
      callback: async (event: WebComponentEvent) => {
        await updateViewProps(grid, gridSmartViews, event);
      },
    },
    {
      element: grid,
      event: SmartViewEvents.SHOW_SAVE_VIEW_AS_DIALOG,
      callback: async () => {
        const {
          smartViewsStrings: {
            saveGridViewFormDialog: { dialogText },
          },
        } = translations;
        await showSaveAsDialog(grid, gridSmartViews, dialogText);
      },
    },
    {
      element: grid,
      event: SmartViewEvents.SMART_VIEW_FAVORITE_CHANGED,
      callback: () => {
        handleSmartViewFavoriteChanged(grid, gridSmartViews);
      },
    },
    {
      element: gridSmartViews,
      event: SmartViewEvents.SMART_VIEW_SELECTED,
      callback: (event: WebComponentEvent) => {
        handleSmartViewClicked(grid, gridSmartViews, buildGridFn, event);
      },
    },
    {
      element: gridSmartViews,
      event: SmartViewEvents.SMART_VIEW_NAME_UPDATED,
      callback: (event: WebComponentEvent) => {
        handleSmartViewNameUpdated(grid, event);
      },
    },
    {
      element: gridSmartViews,
      event: SmartViewEvents.SMART_VIEW_AFTER_CREATE,
      callback: (event: WebComponentEvent) => {
        handleSmartViewAfterCreate(buildGridFn, event);
      },
    },
    {
      element: gridSmartViews,
      event: SmartViewEvents.SMART_VIEW_AFTER_DELETE,
      callback: (event: WebComponentEvent) => {
        handleSmartViewAfterDelete(grid, gridSmartViews, buildGridFn, event);
      },
    },
  ];
}

export function getSideNavigationPanelSmartViewsListener(
  pushBodyLayout: GzPushBodyLayoutWebComponent,
  gridSmartViews: GenericGridSmartViewsWebComponent
) {
  return [
    {
      element: pushBodyLayout,
      event: SideNavEvents.OPEN,
      callback: (event: WebComponentEvent) => {
        handleSidenavOpened(gridSmartViews, event);
      },
    },
    {
      element: pushBodyLayout,
      event: SideNavEvents.CLOSE,
      callback: (event: WebComponentEvent) => {
        handleSidenavClosed(gridSmartViews, event);
      },
    },
    {
      element: pushBodyLayout,
      event: SideNavEvents.NAV_STOP_RESIZE,
      callback: (event: WebComponentEvent) => {
        handleNavResizeEvent(gridSmartViews, event);
      },
    },
  ];
}

export function handleSidenavOpened(gridSmartViews: GenericGridSmartViewsWebComponent, event: WebComponentEvent) {
  if (!gridSmartViews.smartViewState) {
    return;
  }

  gridSmartViews.smartViewState.isCollapsed = false;
  gridSmartViews.updateSmartViewsSettings(gridSmartViews.smartViewState);
}

export function handleSidenavClosed(gridSmartViews, event: WebComponentEvent) {
  if (!gridSmartViews.smartViewState) {
    return;
  }

  gridSmartViews.smartViewState.isCollapsed = true;
  gridSmartViews.updateSmartViewsSettings(gridSmartViews.smartViewState);
}

export function handleNavResizeEvent(gridSmartViews, event: WebComponentEvent) {
  if (!gridSmartViews.smartViewState || event.target.id === 'sideMenu') {
    return;
  }

  gridSmartViews.smartViewState.panelSize = event.detail.navBarWidth;
  gridSmartViews.updateSmartViewsSettings(gridSmartViews.smartViewState);
}

export function setSmartViewsPanelSizeBasedOnState(pushBodyLayout, viewState) {
  if (pushBodyLayout.isOpen === viewState.isCollapsed) {
    pushBodyLayout.toggleSideNav();
  }
  if (viewState.panelSize) {
    pushBodyLayout.style.setProperty('--expanded-width', viewState.panelSize + 'px');
  }
}
